import { create } from 'zustand'
import { get as apiGet, post, put, del, ApiError } from '../lib/api'
import { useAuthStore } from './useAuthStore'
import { OFFLINE_ENABLED, isEffectivelyOffline } from '../config/offline'
import {
  saveSongOffline,
  loadSongOffline,
  saveCollectionsOffline,
  loadCollectionsOffline,
  saveSongsListOffline,
  loadSongsListOffline,
  listPendingSongs,
  saveCollectionOfflinePending,
  clearCollectionPendingSync,
  clearSongPendingSync,
  removeSongOffline,
  removeCollectionOffline,
} from './../lib/offline/songCache'
import type { Song, SongElement, SongAttachment, Collection, Playlist } from '../types/songbook'

interface SongState {
  songs: Song[]
  collections: Collection[]
  playlists: Playlist[]
  currentSong: Song | null
  isLoading: boolean
  isSaving: boolean
  saveRetries: number
  error: string | null
  offlineInfo: string | null

  fetchCollections: (force?: boolean) => Promise<void>
  createCollection: (name: string, artist: string) => Promise<string | null>
  deleteCollection: (id: string) => Promise<void>
  renameCollection: (id: string, name: string) => Promise<void>
  toggleShareCollection: (id: string) => Promise<string | null>
  fetchSongs: (force?: boolean) => Promise<void>
  createSong: (title: string, collectionId: string) => Promise<string | null>
  deleteSong: (id: string) => Promise<void>
  loadSong: (id: string) => Promise<void>
  updateTitle: (title: string) => void
  updateNotes: (notes: string) => void
  updateInfo: (info: string) => void
  updateListenUrl: (listenUrl: string) => void
  updateDuration: (duration: number | null) => void
  addElement: (element: SongElement) => void
  insertElementInSection: (element: SongElement, sectionId: string) => void
  removeElement: (elementId: string) => void
  moveElement: (elementId: string, direction: 'up' | 'down') => void
  moveSectionBlock: (sectionId: string, direction: 'up' | 'down') => void
  updateElement: (elementId: string, data: Partial<SongElement>) => void
  uploadAttachment: (file: File) => Promise<void>
  deleteAttachment: (attachmentId: string) => Promise<void>
  toggleShareSong: (id: string) => Promise<string | null>
  clearCurrentSong: () => void
  clearError: () => void

  // Playlists
  fetchPlaylists: (force?: boolean) => Promise<void>
  createPlaylist: (name: string, songIds: string[]) => Promise<string | null>
  deletePlaylist: (id: string) => Promise<void>
  renamePlaylist: (id: string, name: string) => Promise<void>
  addSongsToPlaylist: (playlistId: string, songIds: string[]) => Promise<void>
  removeSongFromPlaylist: (playlistId: string, songId: string) => Promise<void>
  reorderPlaylistSong: (playlistId: string, songId: string, direction: 'up' | 'down') => Promise<void>
}

let saveTimeout: ReturnType<typeof setTimeout> | null = null
let retryInterval: ReturnType<typeof setInterval> | null = null
let pendingSave = false

function backupToLocal(song: Song) {
  try {
    localStorage.setItem(`songbook-backup-${song.id}`, JSON.stringify({ song, savedAt: Date.now() }))
  } catch { /* quota exceeded */ }
}

function restoreFromLocal(songId: string): Song | null {
  try {
    const raw = localStorage.getItem(`songbook-backup-${songId}`)
    if (!raw) return null
    const { song } = JSON.parse(raw)
    return song as Song
  } catch { return null }
}

function clearLocalBackup(songId: string) {
  localStorage.removeItem(`songbook-backup-${songId}`)
}

function backupToIDB(song: Song): void {
  if (!OFFLINE_ENABLED) return
  void saveSongOffline(song, 'own')
}

async function restoreFromIDB(songId: string): Promise<Song | null> {
  if (!OFFLINE_ENABLED) return null
  return loadSongOffline(songId)
}

let currentSaveAbort: AbortController | null = null

async function saveToCloud(song: Song): Promise<boolean> {
  currentSaveAbort?.abort()
  const controller = new AbortController()
  currentSaveAbort = controller

  const timeoutId = setTimeout(() => controller.abort(), 10000)

  try {
    const body: Record<string, unknown> = {
      title:    song.title,
      elements: song.elements,
      notes:    song.notes ?? null,
      info:     song.info ?? null,
      listen_url:  song.listenUrl ?? '',
      duration:    song.duration ?? null,
      attachments: song.attachments ?? [],
    }

    await put(`/songs/${song.id}`, body, { signal: controller.signal })
    return true
  } catch (e) {
    if (e instanceof ApiError) console.error('Save failed:', e.message)
    else console.error('Save exception:', e)
    return false
  } finally {
    clearTimeout(timeoutId)
    if (currentSaveAbort === controller) currentSaveAbort = null
  }
}

function startRetryLoop() {
  if (retryInterval) return
  retryInterval = setInterval(async () => {
    const { currentSong } = useSongStore.getState()
    if (!currentSong || !pendingSave) { stopRetryLoop(); return }
    const ok = await saveToCloud(currentSong)
    if (ok) {
      clearLocalBackup(currentSong.id)
      pendingSave = false
      useSongStore.setState({ isSaving: false, error: null, saveRetries: 0 })
      stopRetryLoop()
    } else {
      useSongStore.setState((s) => ({ saveRetries: s.saveRetries + 1 }))
    }
  }, 5000)
}

function stopRetryLoop() {
  if (retryInterval) { clearInterval(retryInterval); retryInterval = null }
}

function debouncedSave(get: () => SongState) {
  if (saveTimeout) clearTimeout(saveTimeout)
  saveTimeout = setTimeout(() => {
    saveTimeout = null
    void executeSave(get)
  }, 500)
}

async function executeSave(get: () => SongState) {
  const { currentSong } = get()
  if (!currentSong) return

  backupToLocal(currentSong)
  backupToIDB(currentSong)
  pendingSave = true
  useSongStore.setState({ isSaving: true })

  const ok = await saveToCloud(currentSong)

  if (ok) {
    clearLocalBackup(currentSong.id)
    pendingSave = false
    useSongStore.setState({ isSaving: false, error: null, saveRetries: 0 })
    stopRetryLoop()
  } else {
    useSongStore.setState({ error: 'Save failed — retrying... (local backup saved)', saveRetries: 1 })
    startRetryLoop()
  }
}

function flushPendingSave() {
  if (saveTimeout) {
    clearTimeout(saveTimeout)
    saveTimeout = null
    void executeSave(() => useSongStore.getState())
  }
}

function mapRow(row: Record<string, unknown>): Song {
  return {
    id:           row.id as string,
    userId:       row.user_id as string,
    collectionId: (row.collection_id as string) ?? null,
    title:        row.title as string,
    notes:        (row.notes as string)       ?? '',
    info:         (row.info as string)        ?? '',
    listenUrl:    (row.listen_url as string)  ?? '',
    duration:     (row.duration as number)    ?? null,
    shareToken:   (row.share_token as string) ?? null,
    attachments:  (row.attachments ?? [])     as SongAttachment[],
    elements:     (row.elements ?? [])        as SongElement[],
    createdAt:    row.created_at as string,
    updatedAt:    row.updated_at as string,
  }
}

function mapCollection(row: Record<string, unknown>): Collection {
  return {
    id:         row.id as string,
    userId:     row.user_id as string,
    name:       row.name as string,
    artist:     (row.artist as string)       ?? '',
    shareToken: (row.share_token as string)  ?? null,
    createdAt:  row.created_at as string,
    updatedAt:  row.updated_at as string,
  }
}

function mapPlaylist(row: Record<string, unknown>): Playlist {
  return {
    id:        row.id as string,
    userId:    row.user_id as string,
    name:      row.name as string,
    songIds:   (row.song_ids ?? []) as string[],
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  }
}

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), ms))
  return Promise.race([promise, timeout])
}

export const useSongStore = create<SongState>((set, get) => ({
  songs:       [],
  collections: [],
  playlists:   [],
  currentSong: null,
  isLoading:   false,
  isSaving:    false,
  saveRetries: 0,
  error:       null,
  offlineInfo: null,

  fetchCollections: async (force = false) => {
    if (!force && get().collections.length > 0) return

    if (OFFLINE_ENABLED && isEffectivelyOffline()) {
      const cached = await loadCollectionsOffline()
      if (cached.length > 0) { set({ collections: cached }); return }
    }

    try {
      const rows = await withTimeout(
        apiGet<Record<string, unknown>[]>('/collections'),
        5000
      )
      if (!rows) {
        if (OFFLINE_ENABLED) {
          const cached = await loadCollectionsOffline()
          if (cached.length > 0) set({ collections: cached })
        }
        return
      }

      const collections = rows.map(mapCollection)
      set({ collections })
      // No length guard: an empty server list must still reconcile the
      // cache, otherwise collections deleted online linger offline.
      if (OFFLINE_ENABLED) void saveCollectionsOffline(collections)
    } catch (e) {
      console.error('fetchCollections error:', e)
      if (OFFLINE_ENABLED) {
        const cached = await loadCollectionsOffline()
        if (cached.length > 0) set({ collections: cached })
      }
    }
  },

  createCollection: async (name, artist) => {
    set({ error: null })

    const userId = useAuthStore.getState().user?.id
    if (!userId) { set({ error: 'Not signed in' }); return null }

    const id  = crypto.randomUUID()
    const now = new Date().toISOString()
    const collection: Collection = { id, userId, name, artist, shareToken: null, createdAt: now, updatedAt: now }

    set((state) => ({ collections: [...state.collections, collection] }))

    if (OFFLINE_ENABLED) await saveCollectionOfflinePending(collection)

    try {
      const created = await post<Record<string, unknown>>('/collections', { id, name, artist, user_id: userId })
      if (OFFLINE_ENABLED) await clearCollectionPendingSync(id)
      // Update with server-returned data (same id, but now has proper timestamps)
      set((state) => ({
        collections: state.collections.map((c) => c.id === id ? mapCollection(created) : c),
      }))
    } catch (e) {
      console.warn('createCollection cloud insert failed (queued):', e)
    }

    return id
  },

  deleteCollection: async (id) => {
    set({ error: null })
    try {
      await del(`/collections/${id}`)
      const removedSongIds = get().songs.filter((s) => s.collectionId === id).map((s) => s.id)
      set((state) => ({
        collections: state.collections.filter((c) => c.id !== id),
        songs:       state.songs.filter((s) => s.collectionId !== id),
      }))
      if (OFFLINE_ENABLED) {
        void removeCollectionOffline(id)
        removedSongIds.forEach((songId) => void removeSongOffline(songId))
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) })
    }
  },

  renameCollection: async (id, name) => {
    set({ error: null })
    try {
      await put(`/collections/${id}`, { name })
      set((state) => ({
        collections: state.collections.map((c) => c.id === id ? { ...c, name } : c),
      }))
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) })
    }
  },

  toggleShareCollection: async (id) => {
    set({ error: null })
    const col = get().collections.find((c) => c.id === id)
    if (!col) return null

    try {
      const updated = await post<Record<string, unknown>>(`/collections/${id}/share`, {
        enabled: !col.shareToken,
      })
      const mapped = mapCollection(updated)
      set((state) => ({
        collections: state.collections.map((c) => c.id === id ? mapped : c),
      }))
      return mapped.shareToken
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) })
      return null
    }
  },

  fetchSongs: async (force = false) => {
    if (!force && get().songs.length > 0) { set({ isLoading: false }); return }

    set({ isLoading: true, error: null })

    if (OFFLINE_ENABLED && isEffectivelyOffline()) {
      const cached = await loadSongsListOffline()
      if (cached.length > 0) { set({ songs: cached, isLoading: false }); return }
    }

    try {
      const rows = await withTimeout(
        apiGet<Record<string, unknown>[]>('/songs'),
        5000
      )
      if (!rows) {
        if (OFFLINE_ENABLED) {
          const cached = await loadSongsListOffline()
          if (cached.length > 0) set({ songs: cached })
        }
        return
      }

      const songs = rows.map(mapRow)

      if (OFFLINE_ENABLED) {
        const pending   = await listPendingSongs()
        const cloudIds  = new Set(songs.map((s) => s.id))
        const pendingOnly = pending.filter((r) => !cloudIds.has(r.id)).map((r) => r.song)
        set({ songs: [...songs, ...pendingOnly] })
        void saveSongsListOffline(songs)
      } else {
        set({ songs })
      }
    } catch (e) {
      console.error('fetchSongs error:', e)
      if (OFFLINE_ENABLED) {
        const cached = await loadSongsListOffline()
        if (cached.length > 0) set({ songs: cached })
      }
    } finally {
      set({ isLoading: false })
    }
  },

  createSong: async (title, collectionId) => {
    set({ error: null })

    const userId = useAuthStore.getState().user?.id
    if (!userId) { set({ error: 'Not signed in' }); return null }

    const id  = crypto.randomUUID()
    const now = new Date().toISOString()
    const song: Song = {
      id, userId, collectionId, title,
      notes: '', info: '', listenUrl: '', duration: null,
      shareToken: null, attachments: [], elements: [],
      createdAt: now, updatedAt: now,
    }

    set((state) => ({ songs: [song, ...state.songs] }))
    if (OFFLINE_ENABLED) await saveSongOffline(song, 'own', null, true)

    if (!isEffectivelyOffline()) {
      try {
        const created = await withTimeout(
          post<Record<string, unknown>>('/songs', { id, title, collection_id: collectionId, user_id: userId }),
          5000
        )
        if (created && OFFLINE_ENABLED) await clearSongPendingSync(id)
      } catch (e) {
        console.warn('createSong cloud insert failed (queued):', e)
      }
    }

    return id
  },

  deleteSong: async (id) => {
    set({ error: null })
    try {
      await del(`/songs/${id}`)
      set((state) => ({ songs: state.songs.filter((s) => s.id !== id) }))
      if (OFFLINE_ENABLED) void removeSongOffline(id)
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) })
    }
  },

  loadSong: async (id) => {
    flushPendingSave()
    stopRetryLoop()
    pendingSave = false
    set({ error: null, offlineInfo: null, saveRetries: 0 })

    // Fast path: already in store
    const cached = get().songs.find((s) => s.id === id)
    if (cached) {
      set({ currentSong: cached, isLoading: false })
      // Background refresh
      void apiGet<Record<string, unknown>>(`/songs/${id}`)
        .then((data) => {
          if (data) {
            const fresh = mapRow(data)
            set((state) => {
              if (state.currentSong?.id === id) return { currentSong: fresh }
              return {}
            })
            backupToIDB(fresh)
            clearLocalBackup(id)
          }
        })
        .catch(() => { /* ignore background refresh errors */ })
      return
    }

    set({ isLoading: true, currentSong: null })

    if (OFFLINE_ENABLED && isEffectivelyOffline()) {
      const local = restoreFromLocal(id)
      if (local) { set({ currentSong: local, isLoading: false, offlineInfo: 'Aus lokalem Backup geladen' }); return }
      const idb = await restoreFromIDB(id)
      if (idb)  { set({ currentSong: idb,   isLoading: false, offlineInfo: 'Aus Offline-Cache geladen'  }); return }
    }

    try {
      const data = await withTimeout(
        apiGet<Record<string, unknown>>(`/songs/${id}`),
        5000
      )

      if (!data) {
        const local = restoreFromLocal(id)
        if (local) { set({ currentSong: local, offlineInfo: 'Aus lokalem Backup geladen' }); return }
        const idb = await restoreFromIDB(id)
        if (idb)  { set({ currentSong: idb,   offlineInfo: 'Aus Offline-Cache geladen'  }); return }
        set({ error: 'Timeout' })
        return
      }

      const cloudSong = mapRow(data)
      const local     = restoreFromLocal(id)

      if (local && local.elements.length > cloudSong.elements.length) {
        set({ currentSong: local })
        backupToIDB(local)
        debouncedSave(() => useSongStore.getState())
      } else {
        set({ currentSong: cloudSong })
        backupToIDB(cloudSong)
        clearLocalBackup(id)
      }
    } catch (e) {
      console.error('loadSong error:', e)
      const local = restoreFromLocal(id)
      if (local) { set({ currentSong: local, offlineInfo: 'Aus lokalem Backup geladen' }); return }
      const idb = await restoreFromIDB(id)
      if (idb)  { set({ currentSong: idb,   offlineInfo: 'Aus Offline-Cache geladen'  }); return }
    } finally {
      set({ isLoading: false })
    }
  },

  updateTitle: (title) => {
    const { currentSong } = get()
    if (!currentSong) return
    set({ currentSong: { ...currentSong, title } })
    debouncedSave(get)
  },

  updateNotes: (notes) => {
    const { currentSong } = get()
    if (!currentSong) return
    set({ currentSong: { ...currentSong, notes } })
    debouncedSave(get)
  },

  updateInfo: (info) => {
    const { currentSong } = get()
    if (!currentSong) return
    set({ currentSong: { ...currentSong, info } })
    debouncedSave(get)
  },

  updateListenUrl: (listenUrl) => {
    const { currentSong } = get()
    if (!currentSong) return
    set({ currentSong: { ...currentSong, listenUrl } })
    debouncedSave(get)
  },

  updateDuration: (duration) => {
    const { currentSong } = get()
    if (!currentSong) return
    set({ currentSong: { ...currentSong, duration } })
    debouncedSave(get)
  },

  addElement: (element) => {
    const { currentSong } = get()
    if (!currentSong) return
    set({ currentSong: { ...currentSong, elements: [...currentSong.elements, element] } })
    debouncedSave(get)
  },

  insertElementInSection: (element, sectionId) => {
    const { currentSong } = get()
    if (!currentSong) return

    const elements = [...currentSong.elements]
    const sectionIdx = elements.findIndex((e) => e.id === sectionId)

    if (sectionIdx === -1) {
      elements.push(element)
    } else {
      let insertAt = elements.length
      for (let i = sectionIdx + 1; i < elements.length; i++) {
        if (elements[i].type === 'section') { insertAt = i; break }
      }
      elements.splice(insertAt, 0, element)
    }

    set({ currentSong: { ...currentSong, elements } })
    debouncedSave(get)
  },

  removeElement: (elementId) => {
    const { currentSong } = get()
    if (!currentSong) return
    set({ currentSong: { ...currentSong, elements: currentSong.elements.filter((e) => e.id !== elementId) } })
    debouncedSave(get)
  },

  moveElement: (elementId, direction) => {
    const { currentSong } = get()
    if (!currentSong) return

    const elements  = [...currentSong.elements]
    const idx       = elements.findIndex((e) => e.id === elementId)
    if (idx === -1) return

    const targetIdx = direction === 'up' ? idx - 1 : idx + 1
    if (targetIdx < 0 || targetIdx >= elements.length) return

    ;[elements[idx], elements[targetIdx]] = [elements[targetIdx], elements[idx]]
    set({ currentSong: { ...currentSong, elements } })
    debouncedSave(get)
  },

  moveSectionBlock: (sectionId, direction) => {
    const { currentSong } = get()
    if (!currentSong) return

    const elements       = [...currentSong.elements]
    const sectionIndices = elements.reduce<number[]>((acc, el, i) => {
      if (el.type === 'section') acc.push(i)
      return acc
    }, [])

    const blockIdx = sectionIndices.findIndex((i) => elements[i].id === sectionId)
    if (blockIdx === -1) return

    const blockStart = sectionIndices[blockIdx]
    const blockEnd   = blockIdx + 1 < sectionIndices.length ? sectionIndices[blockIdx + 1] : elements.length
    const block      = elements.splice(blockStart, blockEnd - blockStart)

    const remaining = elements.reduce<number[]>((acc, el, i) => {
      if (el.type === 'section') acc.push(i)
      return acc
    }, [])

    if (direction === 'up' && blockIdx > 0) {
      elements.splice(remaining[blockIdx - 1], 0, ...block)
    } else if (direction === 'down' && blockIdx < sectionIndices.length - 1) {
      const nextEnd = blockIdx + 1 < remaining.length ? remaining[blockIdx + 1] : elements.length
      elements.splice(nextEnd, 0, ...block)
    } else {
      elements.splice(blockStart, 0, ...block)
      return
    }

    set({ currentSong: { ...currentSong, elements } })
    debouncedSave(get)
  },

  updateElement: (elementId, data) => {
    const { currentSong } = get()
    if (!currentSong) return
    set({
      currentSong: {
        ...currentSong,
        elements: currentSong.elements.map((e) =>
          e.id === elementId ? { ...e, ...data } as SongElement : e
        ),
      },
    })
    debouncedSave(get)
  },

  uploadAttachment: async (_file) => {
    // File upload backend not yet implemented.
    set({ error: 'File upload is not yet supported in this version.' })
  },

  deleteAttachment: async (attachmentId) => {
    const { currentSong } = get()
    if (!currentSong) return

    const newAttachments = currentSong.attachments.filter((a) => a.id !== attachmentId)
    set({ currentSong: { ...currentSong, attachments: newAttachments } })

    const ok = await saveToCloud({ ...currentSong, attachments: newAttachments })
    if (ok) backupToLocal({ ...currentSong, attachments: newAttachments })
  },

  toggleShareSong: async (id) => {
    set({ error: null })
    const song = get().currentSong?.id === id ? get().currentSong : get().songs.find((s) => s.id === id)
    if (!song) return null

    try {
      const updated = await post<Record<string, unknown>>(`/songs/${id}/share`, {
        enabled: !song.shareToken,
      })
      const mapped = mapRow(updated)
      if (get().currentSong?.id === id) set({ currentSong: mapped })
      set((state) => ({
        songs: state.songs.map((s) => s.id === id ? mapped : s),
      }))
      return mapped.shareToken
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) })
      return null
    }
  },

  // ── Playlists ──

  fetchPlaylists: async (force = false) => {
    if (!force && get().playlists.length > 0) return
    try {
      const rows = await apiGet<Record<string, unknown>[]>('/playlists')
      set({ playlists: rows.map(mapPlaylist) })
    } catch (e) {
      console.error('fetchPlaylists error:', e)
    }
  },

  createPlaylist: async (name, songIds) => {
    set({ error: null })
    const userId = useAuthStore.getState().user?.id
    if (!userId) { set({ error: 'Not signed in' }); return null }

    const id  = crypto.randomUUID()
    const now = new Date().toISOString()
    const playlist: Playlist = { id, userId, name, songIds, createdAt: now, updatedAt: now }
    set((s) => ({ playlists: [...s.playlists, playlist] }))

    try {
      await post('/playlists', { id, user_id: userId, name, song_ids: songIds })
    } catch (e) {
      console.warn('createPlaylist failed:', e)
    }
    return id
  },

  deletePlaylist: async (id) => {
    set((s) => ({ playlists: s.playlists.filter((p) => p.id !== id) }))
    try { await del(`/playlists/${id}`) } catch (e) { console.warn('deletePlaylist:', e) }
  },

  renamePlaylist: async (id, name) => {
    set((s) => ({ playlists: s.playlists.map((p) => p.id === id ? { ...p, name } : p) }))
    try { await put(`/playlists/${id}`, { name }) } catch (e) { console.warn('renamePlaylist:', e) }
  },

  addSongsToPlaylist: async (playlistId, songIds) => {
    const playlist = get().playlists.find((p) => p.id === playlistId)
    if (!playlist) return
    const merged = [...playlist.songIds, ...songIds.filter((id) => !playlist.songIds.includes(id))]
    set((s) => ({ playlists: s.playlists.map((p) => p.id === playlistId ? { ...p, songIds: merged } : p) }))
    try { await put(`/playlists/${playlistId}`, { song_ids: merged }) } catch (e) { console.warn('addSongsToPlaylist:', e) }
  },

  removeSongFromPlaylist: async (playlistId, songId) => {
    const playlist = get().playlists.find((p) => p.id === playlistId)
    if (!playlist) return
    const filtered = playlist.songIds.filter((id) => id !== songId)
    set((s) => ({ playlists: s.playlists.map((p) => p.id === playlistId ? { ...p, songIds: filtered } : p) }))
    try { await put(`/playlists/${playlistId}`, { song_ids: filtered }) } catch (e) { console.warn('removeSongFromPlaylist:', e) }
  },

  reorderPlaylistSong: async (playlistId, songId, direction) => {
    const playlist = get().playlists.find((p) => p.id === playlistId)
    if (!playlist) return
    const ids    = [...playlist.songIds]
    const idx    = ids.indexOf(songId)
    if (idx === -1) return
    const target = direction === 'up' ? idx - 1 : idx + 1
    if (target < 0 || target >= ids.length) return
    ;[ids[idx], ids[target]] = [ids[target], ids[idx]]
    set((s) => ({ playlists: s.playlists.map((p) => p.id === playlistId ? { ...p, songIds: ids } : p) }))
    try { await put(`/playlists/${playlistId}`, { song_ids: ids }) } catch (e) { console.warn('reorderPlaylistSong:', e) }
  },

  clearCurrentSong: () => {
    flushPendingSave()
    set({ currentSong: null, isLoading: false })
  },

  clearError: () => set({ error: null }),
}))
